import { clamp } from '../../shared/utils/clamp.js';

const styles = /* css */ `
  :host { display: block; box-sizing: border-box; }
  :host *, :host *::before, :host *::after { box-sizing: border-box; }
  :host([hidden]), [hidden], ::slotted([hidden]) { display: none; }
  video { display: block; }
  #output:empty { display: none; }
`;

const template = document.createElement('template');

template.innerHTML = /* html */ `
  <style>${styles}</style>
  <div part="actions-container"><slot name="actions"></slot></div>
  <slot></slot>
`;

class VideoCapture extends HTMLElement {
  #supportedConstraints = {};
  #stream = null;
  #videoEl = null;

  constructor() {
    super();

    this.#supportedConstraints = this.getSupportedConstraints();

    if (!this.shadowRoot) {
      const shadowRoot = this.attachShadow({ mode: 'open' });
      shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  static get observedAttributes() {
    return ['no-image', 'pan', 'tilt', 'zoom', 'torch'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected) {
      return;
    }

    const trackCapabilities = this.getTrackCapabilities();

    if (name === 'zoom' && oldValue !== newValue && 'zoom' in this.#supportedConstraints) {
      const zoomInAllowedRange =
        'zoom' in trackCapabilities && trackCapabilities.zoom?.min && trackCapabilities.zoom?.max
          ? this.zoom >= trackCapabilities.zoom.min && this.zoom <= trackCapabilities.zoom.max
          : false;

      if (typeof this.zoom === 'number' && zoomInAllowedRange) {
        this.#applyConstraint('zoom', this.zoom);
      }
    }

    if (name === 'torch' && oldValue !== newValue && 'torch' in this.#supportedConstraints) {
      this.#applyConstraint('torch', this.torch);
    }
  }

  async connectedCallback() {
    this.#upgradeProperty('autoPlay');
    this.#upgradeProperty('facingMode');
    this.#upgradeProperty('zoom');
    this.#upgradeProperty('torch');

    this.#initializeVideoElement();

    this.#videoEl?.addEventListener('loadedmetadata', this.#onVideoLoadedMetaData);

    if (!VideoCapture.isSupported()) {
      return this.#dispatchError(
        'initialization',
        'not-supported',
        new Error('MediaDevices.getUserMedia() is not supported in this browser')
      );
    }

    if (this.autoPlay) {
      this.startVideoStream();
    }
  }

  disconnectedCallback() {
    this.stopVideoStream();
    this.#videoEl?.removeEventListener('loadedmetadata', this.#onVideoLoadedMetaData);
  }

  get autoPlay() {
    return this.hasAttribute('auto-play');
  }

  set autoPlay(value) {
    this.toggleAttribute('auto-play', !!value);
  }

  get facingMode() {
    const value = this.getAttribute('facing-mode');

    if (value !== 'user') {
      return 'environment';
    }

    return value;
  }

  set facingMode(value) {
    this.setAttribute('facing-mode', value);
  }

  get zoom() {
    return Number(this.getAttribute('zoom')) || 1;
  }

  set zoom(value) {
    this.setAttribute('zoom', value != null ? value.toString() : value);
  }

  get torch() {
    return this.hasAttribute('torch');
  }

  set torch(value) {
    this.toggleAttribute('torch', !!value);
  }

  get loading() {
    return this.hasAttribute('loading');
  }

  /**
   * Creates the video element after the custom element is connected.
   *
   * This ensures the media element is initialized while connected to the
   * document, avoiding browser-specific playback issues caused by creating it
   * earlier. The element is created only once and reused if the component
   * reconnects.
   */
  #initializeVideoElement() {
    if (this.#videoEl) {
      return;
    }

    const video = document.createElement('video');

    video.setAttribute('part', 'video');
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.setAttribute('disablepictureinpicture', '');

    this.shadowRoot?.prepend(video);
    this.#videoEl = video;
  }

  /**
   * Dispatches an error event.
   *
   * @param {string} source - The source of the error.
   * @param {string} reason - The reason for the error.
   * @param {Error} error - The error object.
   */
  #dispatchError(source, reason, error) {
    this.#emitEvent('video-capture-error', { source, reason, error });
  }

  /**
   * Handles the loadedmetadata event of the video element.
   *
   * @param {Event} evt - The event object.
   */
  #onVideoLoadedMetaData = async evt => {
    const video = evt.target;

    try {
      await video.play();
      this.#emitEvent('video-capture-play', { video });
    } catch (error) {
      const reason =
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'user-gesture-required'
          : 'playback-failed';

      this.#dispatchError('playback', reason, error);
    } finally {
      this.removeAttribute('loading');
    }
  };

  /**
   * Applies a constraint to the video track.
   *
   * @param {string} constraint - The name of the constraint.
   * @param {any} value - The value of the constraint.
   */
  #applyConstraint(constraint, value) {
    if (!this.#stream) {
      return;
    }

    const [track] = this.#stream.getVideoTracks();
    const trackCapabilities = this.getTrackCapabilities();
    const trackSettings = this.getTrackSettings();

    const constraintValue =
      constraint === 'pan' || constraint === 'tilt' || constraint === 'zoom'
        ? clamp(
            Number(value),
            trackCapabilities[constraint]?.min || 1,
            trackCapabilities[constraint]?.max || 1
          )
        : value;

    if (constraint in trackSettings) {
      track
        .applyConstraints({
          advanced: [{ [constraint]: constraintValue }]
        })
        .catch(() => {
          // Fail silently...
        });
    }
  }

  /**
   * Dispatches a custom event with the given name.
   *
   * @param {string} eventName - The name of the event to dispatch.
   * @param {Nullable<any>} detail - The detail object to include with the event.
   */
  #emitEvent(eventName, detail = null) {
    const options = { bubbles: true, composed: true, detail };
    const evt = new CustomEvent(eventName, options);
    this.dispatchEvent(evt);
  }

  /**
   * This is to safe guard against cases where, for instance, a framework may have added the element to the page and
   * set a value on one of its properties, but lazy loaded its definition. Without this guard, the upgraded element would
   * miss that property and the instance property would prevent the class property setter from ever being called.
   *
   * https://developers.google.com/web/fundamentals/web-components/best-practices#lazy-properties
   *
   * @param {string} prop - The property to upgrade.
   */
  #upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  /**
   * Starts the video stream.
   *
   * @param {string} [videoInputId] - The video input device ID.
   * @returns Promise<boolean> Resolves to `true` if the video stream started successfully, `false` otherwise.
   */
  async startVideoStream(videoInputId) {
    if (!VideoCapture.isSupported() || this.#stream) {
      return false;
    }

    this.setAttribute('loading', '');

    const constraints = {
      video: {
        facingMode: {
          ideal: this.facingMode
        },
        pan: true,
        tilt: true,
        zoom: true,
        torch: this.torch
      },
      audio: false
    };

    if (typeof videoInputId === 'string' && videoInputId.trim().length > 0) {
      constraints.video.deviceId = { exact: videoInputId };
    }

    if (typeof this.cameraResolution === 'string' && this.cameraResolution.trim().length > 0) {
      const [width = 0, height = 0] = this.cameraResolution.split('x').map(x => Number(x));

      if (width > 0 && height > 0) {
        constraints.video.width = width;
        constraints.video.height = height;
      }
    }

    try {
      this.#stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (this.#videoEl) {
        this.#videoEl.srcObject = this.#stream;
      }

      this.#applyConstraint('pan', this.pan);
      this.#applyConstraint('tilt', this.tilt);
      this.#applyConstraint('zoom', this.zoom);

      return true;
    } catch (error) {
      const reason =
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'camera-access-denied'
          : 'camera-failed';

      this.#dispatchError('camera', reason, error);
      this.removeAttribute('loading');

      return false;
    }
  }

  /**
   * Restarts the video stream.
   *
   * @param {string} [videoInputId] - The video input device ID.
   */
  restartVideoStream(videoInputId) {
    if (this.#stream && this.#videoEl) {
      this.stopVideoStream();
    }

    this.startVideoStream(videoInputId);
  }

  /**
   * Stops the video stream.
   */
  stopVideoStream() {
    if (!this.#videoEl || !this.#stream) {
      return;
    }

    const [track] = this.#stream.getVideoTracks();

    track?.stop();
    this.#videoEl.srcObject = null;
    this.#stream = null;
  }

  /**
   * Starts playback of the current video stream.
   *
   * This may need to be called directly from a user interaction when
   * autoplay is blocked by the browser.
   *
   * @param {options} [options] - The options for playing the video.
   * @param {boolean} [options.emit=false] - Whether to emit a custom event after playing the video.
   * @returns {Promise<void>} A promise that resolves when the video starts playing, or rejects if there is an error.
   */
  async playVideo(options = {}) {
    const { emit = false } = options;

    if (!this.#videoEl || !this.#stream) {
      return;
    }

    try {
      await this.#videoEl.play();

      if (emit) {
        this.#emitEvent('video-capture-play', { video: this.#videoEl });
      }
    } catch (error) {
      const reason =
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'user-gesture-required'
          : 'playback-failed';

      this.#dispatchError('playback', reason, error);
    }
  }

  /**
   * Pauses the current video stream.
   */
  stopVideo() {
    if (!this.#videoEl || !this.#stream) {
      return;
    }

    this.#videoEl.pause();
  }

  /**
   * Returns an object based on the `MediaTrackSupportedConstraints` dictionary,
   * whose member fields each specify one ofthe constrainable properties the user agent understands.
   *
   * @see https://developer.mozilla.org/docs/Web/API/MediaDevices/getSupportedConstraints
   * @returns {MediaTrackSupportedConstraints | {}} The supported constraints or an empty object if the API is not supported.
   */
  getSupportedConstraints() {
    if (!VideoCapture.isSupported()) {
      return {};
    }

    return navigator.mediaDevices.getSupportedConstraints() || {};
  }

  /**
   * Returns a `MediaTrackCapabilities` object which specifies the values or range of values
   * which each constrainable property, based upon the platform and user agent.
   *
   * @see https://developer.mozilla.org/docs/Web/API/MediaStreamTrack/getCapabilities
   * @returns {MediaTrackCapabilities | {}} The track capabilities or an empty object if the API is not supported.
   */
  getTrackCapabilities() {
    if (!this.#stream) {
      return {};
    }

    const [track] = this.#stream.getVideoTracks();

    if (track && typeof track.getCapabilities === 'function') {
      return track.getCapabilities() || {};
    }

    return {};
  }

  /**
   * Returns a `MediaTrackSettings` object containing the current values of each of
   * the constrainable properties for the current MediaStreamTrack.
   *
   * @see https://developer.mozilla.org/docs/Web/API/MediaStreamTrack/getSettings
   * @returns {MediaTrackSettings | {}} The track settings or an empty object if the API is not supported.
   */
  getTrackSettings() {
    if (!this.#stream) {
      return {};
    }

    const [track] = this.#stream.getVideoTracks();

    if (track && typeof track.getSettings === 'function') {
      return track.getSettings() || {};
    }

    return {};
  }

  /**
   * Gets the list of available video input devices.
   *
   * @returns {Promise<MediaDeviceInfo[]>} A promise that resolves to an array of video input devices.
   */
  static async getVideoInputDevices() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return [];
    }

    const devices = (await navigator.mediaDevices.enumerateDevices()) || [];
    return devices.filter(device => device.kind === 'videoinput' && !!device.deviceId);
  }

  /**
   * Checks if the `MediaDevices.getUserMedia()` method is supported.
   *
   * @returns {boolean} `true` if the method is supported, `false` otherwise.
   */
  static isSupported() {
    return Boolean(navigator.mediaDevices?.getUserMedia);
  }

  /**
   * Defines a custom element with the given name.
   * The name must contain a dash (-).
   *
   * @param {string} [elementName='video-capture'] - The name of the custom element.
   */
  static define(elementName = 'video-capture') {
    if (typeof window !== 'undefined' && !window.customElements.get(elementName)) {
      window.customElements.define(elementName, VideoCapture);
    }
  }
}

export { VideoCapture };
